// Copyright 2016 Sorama B.V.

import { autoinject, bindable, computedFrom, BindingEngine, Disposable, bindingMode } from "aurelia-framework";
import { IPlotParent } from "controls/plot/IPlotParent";
import { Size, Margin, Pair, Rect } from "controls/plot/IPlotPart";
import { PlotPartBase } from "controls/plot/PlotPartBase";
import { IExportable } from "libraries/Export/IExportable";
import { SVGNode } from "libraries/SVG/SVGNode";
import { Unit, UnitValue } from "units/Unit";

export interface PointSelection {
    x?: UnitValue;
    y?: UnitValue;
}

interface UIPointSelection extends PointSelection {
    xCoordinate: number;
    yCoordinate: number;
}

@autoinject
export class PointSelectionPart extends PlotPartBase implements IExportable {
    //#region Constants
    private containerClassName = "point-container";
    //#endregion

    //#region HTMLElements
    private inputContainer: HTMLDivElement;
    //#endregion

    //#region Bindables
    @bindable public selection: (source: PointSelectionPart, point: { x?: UnitValue; y?: UnitValue }) => void;
    @bindable public selectorsize = 0;
    @bindable public snapinterval = 0;
    @bindable public selectionmode = "xy";
    @bindable public selectorfill = "carrot";
    @bindable public selectorstroke = "carrot";
    @bindable public selectorfillopacity = 1.0;
    @bindable public selectorstrokeopacity = 1.0;

    @bindable({ defaultBindingBehavior: bindingMode.twoWay })
    public selections: PointSelection[];

    private pointSelections: UIPointSelection[];

    //#endregion

    //#region Fields

    private bindingEngine: BindingEngine;

    private viewBox = "0 0 1 1";
    private mouseOver: boolean;
    private lastMouseX = 0;
    private lastMouseY = 0;

    private selectionsCollectionObserver: Disposable;

    private selectedRectangles: { x0: number; y0: number; width: number; height: number }[] = [];
    private selectedLines: { x0: number; y0: number; x1: number; y1: number }[] = [];

    //#endregion

    //#region Constructor and Initialisation
    constructor(bindingEngine: BindingEngine) {
        super(0);
        this.bindingEngine = bindingEngine;
    }

    public attached(): void {
        super.attached();

        this.plotParent.addInputEventListener("mouseenter", this.onMouseEnter);
        this.plotParent.addInputEventListener("mouseleave", this.onMouseLeave);
        this.plotParent.addInputEventListener("mouseout", this.onMouseLeave);
        this.plotParent.addInputEventListener("mousemove", this.onMouseMove);
        this.plotParent.addInputEventListener("click", this.onClick);
    }

    public detached(): void {
        this.plotParent.removeInputEventListener("mouseenter", this.onMouseEnter);
        this.plotParent.removeInputEventListener("mouseleave", this.onMouseLeave);
        this.plotParent.removeInputEventListener("mouseout", this.onMouseLeave);
        this.plotParent.removeInputEventListener("mousemove", this.onMouseMove);
        this.plotParent.removeInputEventListener("click", this.onClick);

        super.detached();
    }

    public bind(context: IPlotParent): void {
        super.bind(context);
        this.selectionsChanged();
    }

    public unbind(): void {
        if (this.selectionsCollectionObserver) {
            this.selectionsCollectionObserver.dispose();
            this.selectionsCollectionObserver = undefined;
        }
    }

    //#endregion

    //#region IPlotPart

    public onMeasureMargin(): Margin {
        // Small extra margin required
        return new Margin(0, 0, 0, 0);
    }

    public onSize(size: Size, margin: Margin): void {
        this.size = size;
        this.margin = margin;

        this.actualSize = new Size(
            this.size.width - this.margin.left - this.margin.right,
            this.size.height - this.margin.top - this.margin.bottom
        );

        this.viewBox = "0 0 " + this.actualSize.width + " " + this.actualSize.height;

        // Trigger update, actualsize changed
        this.selectionsCollectionChanged();
    }

    public onViewPortComputed(viewPort: Rect, viewPortBounds: Rect, units: Pair<Unit>) {
        super.onViewPortComputed(viewPort, viewPortBounds, units);

        this.onMouseMove(<MouseEvent>{ offsetX: this.lastMouseX, offsetY: this.lastMouseY });

        // Trigger update, viewport changed
        this.selectionsCollectionChanged();
    }

    //#endregion

    //#region Change Handlers

    public selectionmodeChanged(newValue: string, oldValue: string): void {
        if (!this.isValidXYSelector(newValue) || newValue == "") {
            console.error("PointSelectionPart: Invalid selection mode");
            this.selectionmode = oldValue;
        }
    }

    //#endregion

    //#region Mouse Handlers

    private onMouseEnter = (event: MouseEvent): void => {
        this.mouseOver = true;
    };

    private onMouseLeave = (event: MouseEvent): void => {
        this.mouseOver = false;
        this.selectedRectangles = [];
        this.selectedLines = [];
    };

    private onMouseMove = (event: MouseEvent): void => {
        if (
            !this.mouseOver ||
            !this.size ||
            !this.margin ||
            !this.viewPort ||
            this.viewPort.width === 0 ||
            this.viewPort.height === 0
        )
            return;

        this.lastMouseX = event.offsetX;
        this.lastMouseY = event.offsetY;

        const plotWidth = this.size.width - this.margin.left - this.margin.right;
        const plotHeight = this.size.height - this.margin.top - this.margin.bottom;

        let x = (event.offsetX / plotWidth) * this.viewPort.width + this.viewPort.x;
        let y = (event.offsetY / plotHeight) * this.viewPort.height + this.viewPort.y;

        if (this.snapinterval) {
            x = Math.round(x / this.snapinterval) * this.snapinterval;
            y = Math.round(y / this.snapinterval) * this.snapinterval;
        }

        // Convert to svg coordinates
        const xySvg = this.viewPortToSvgCoordinates({ x: x, y: y });
        const selectorSizeSvgX = (this.selectorsize / this.viewPort.width) * this.actualSize.width;
        const selectorSizeSvgY = (this.selectorsize / this.viewPort.height) * this.actualSize.height;

        if (this.selectorsize !== 0) {
            this.selectedLines = [];

            this.selectedRectangles = [
                {
                    width: this.selectionmode !== "y" ? selectorSizeSvgX : this.actualSize.width,
                    height: this.selectionmode !== "x" ? selectorSizeSvgY : this.actualSize.height,
                    x0: this.selectionmode !== "y" ? xySvg.x - selectorSizeSvgX * 0.5 : 0,
                    y0: this.selectionmode !== "x" ? xySvg.y - selectorSizeSvgY * 0.5 : 0,
                },
            ];
        } else {
            this.selectedRectangles = [];

            const lines: { x0: number; y0: number; x1: number; y1: number }[] = [];

            if (this.selectionmode.indexOf("x") !== -1) {
                lines.push({
                    x0: xySvg.x,
                    x1: xySvg.x,
                    y0: 0,
                    y1: this.actualSize.height,
                });
            }

            if (this.selectionmode.indexOf("y") !== -1) {
                lines.push({
                    y0: xySvg.y,
                    y1: xySvg.y,
                    x0: 0,
                    x1: this.actualSize.width,
                });
            }

            this.selectedLines = lines;
        }
    };

    private onClick = (event: MouseEvent): void => {
        if (
            !this.selection ||
            ((!this.selectedLines || this.selectedLines.length === 0) &&
                (!this.selectedRectangles || this.selectedRectangles.length === 0))
        )
            return;

        const xx =
            this.selectedRectangles.length !== 0
                ? this.selectedRectangles[0].x0 + this.selectedRectangles[0].width * 0.5
                : (this.selectedLines[0].x0 + this.selectedLines[0].x1) / 2;

        const yLine = this.selectedLines.length > 1 ? this.selectedLines[1] : this.selectedLines[0];

        const yy =
            this.selectedRectangles.length !== 0
                ? this.selectedRectangles[0].y0 + this.selectedRectangles[0].height * 0.5
                : (yLine.y0 + yLine.y1) / 2;

        const clickLocation: PointSelection = {};

        if (this.selectionmode.indexOf("x") !== -1) {
            clickLocation.x = new UnitValue(
                (xx / this.actualSize.width) * this.viewPort.width + this.viewPort.x,
                this.sourceUnits.x
            );
        }

        if (this.selectionmode.indexOf("y") !== -1) {
            clickLocation.y = new UnitValue(
                ((this.actualSize.height - yy) / this.actualSize.height) * this.viewPort.height + this.viewPort.y,
                this.sourceUnits.y
            );
        }

        this.selection(this, clickLocation);
    };

    //#endregion

    //#region Change Handlers

    private selectionsChanged(): void {
        if (this.selectionsCollectionObserver) {
            this.selectionsCollectionObserver.dispose();
            this.selectionsCollectionObserver = undefined;
        }

        if (this.selections) {
            this.selectionsCollectionObserver = this.bindingEngine
                .collectionObserver(this.selections)
                .subscribe(this.selectionsCollectionChanged);
        }

        this.selectionsCollectionChanged();
    }

    private selectionsCollectionChanged = (): void => {
        if (!this.actualSize || !this.viewPort || !this.selections) {
            this.pointSelections = undefined;
            return;
        }

        this.pointSelections = this.selections.map((x: UIPointSelection) => {
            x.xCoordinate = this.viewPortToSvgX(x.x.value);
            x.yCoordinate = this.actualSize.height - this.viewPortToSvgY(x.y.value);

            return x;
        });
    };

    //#endregion

    //#region IExportable

    public exportToSvg(): Promise<SVGNode> {
        if (!this.inputContainer) return undefined;

        const svgs = <SVGNode[]>[];

        // Find all nodes with class containerClassName
        for (let i = 0; i < this.inputContainer.children.length; i++) {
            const child = <HTMLElement>this.inputContainer.children[i];
            if (child.classList.contains(this.containerClassName)) {
                let childSvgs = SVGNode.getSvgsInElement(child);
                if (childSvgs.length != 1) continue;

                // Strip off svg tag
                const wTag = childSvgs[0].attributes["width"];
                const hTag = childSvgs[0].attributes["height"];
                childSvgs = childSvgs[0].children;

                // Offset all child svgs with the offset of the div element
                const group = new SVGNode("g");

                const w = Number(wTag ? wTag.replace("px", "") : "0");
                const h = Number(hTag ? hTag.replace("px", "") : "0");

                let top = Number(child.style.top ? child.style.top.replace("px", "") : "0");
                let left = Number(child.style.left ? child.style.left.replace("px", "") : "0");

                top -= h / 2;
                left -= w / 2;

                group.attributes["transform"] = "translate(" + left + "," + top + ")";

                for (const childSvg of childSvgs) {
                    group.addChild(childSvg);
                }

                svgs.push(group);
            }
        }

        const partSvg = new SVGNode("svg");

        const time = new Date().getTime();
        const clipPathId = "clippath-point" + time;

        // Generate a defs section with a clip path
        const defsNode = new SVGNode("defs");
        const clipPathNode = new SVGNode("clipPath");
        clipPathNode.setAttribute("id", clipPathId);

        const rectNode = new SVGNode("rect");
        rectNode.setAttribute("x", "1");
        rectNode.setAttribute("y", "0");
        rectNode.setAttribute("width", (this.actualSize.width - 1).toFixed(0));
        rectNode.setAttribute("height", this.actualSize.height.toFixed(0));

        clipPathNode.addChild(rectNode);
        defsNode.addChild(clipPathNode);
        partSvg.addChild(defsNode);

        const g = new SVGNode("g");

        g.setAttribute("clip-path", "url(#" + clipPathId + ")");

        for (const svg of svgs) {
            g.addChild(svg);
        }

        partSvg.addChild(g);

        return Promise.resolve(partSvg);
    }

    //#endregion

    //#region Helpers

    private isValidXYSelector(value: string) {
        if (value === null || value === undefined || typeof value !== "string") return false;

        const vv = value.toLowerCase();

        if (vv === "" || vv === "x" || vv === "y" || vv === "xy" || vv === "yx") return true;

        return false;
    }

    private viewPortToSvgCoordinates(coordinate: { x: number; y: number }): { x: number; y: number } {
        return {
            x: this.viewPortToSvgX(coordinate.x),
            y: this.viewPortToSvgY(coordinate.y),
        };
    }

    private viewPortToSvgX(x: number): number {
        if (!this.viewPort || !this.actualSize) return 0;

        return Math.round(((x - this.viewPort.x) / this.viewPort.width) * this.actualSize.width);
    }

    private viewPortToSvgY(y: number): number {
        if (!this.viewPort || !this.actualSize) return 0;

        return Math.round(((y - this.viewPort.y) / this.viewPort.height) * this.actualSize.height);
    }
    //#endregion
}
